<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import StatusBadge from '../../framework/components/app/StatusBadge.svelte';
  import {
    builderProgress,
    isStepAnswered,
    stepPreview,
  } from './answers';
  import type { BriefBuilderAnswers } from './types';

  export let answers: BriefBuilderAnswers;
  export let isGenerating = false;

  const dispatch = createEventDispatcher<{
    continue: void;
    discard: void;
  }>();

  $: progress = builderProgress(answers);
  $: decisionQuote = (answers.decision || '').replace(/\s+/g, ' ').trim();
  $: previewSteps = progress.answeredSteps.slice(0, 4);
  $: nextStep = progress.steps.find((s) => !isStepAnswered(s, answers)) || null;
</script>

<section class="paused" aria-labelledby="paused-title">
  <div class="paused-glow" aria-hidden="true"></div>

  <header class="paused-header">
    <div class="paused-kicker">
      <span class="agent-chip" aria-hidden="true">
        <i class="pencil alternate icon"></i>
      </span>
      <StatusBadge label={isGenerating ? 'Generating' : 'In progress'} tone={isGenerating ? 'info' : 'warn'} />
    </div>
    <h2 id="paused-title" class="paused-title">Consultation paused</h2>
    <p class="paused-lede prose">
      {#if isGenerating}
        Your research brief is still being written. You can continue the consultation once generation finishes.
      {:else if progress.answered === 0}
        You started a Built-to-Brief consultation but haven’t answered yet. Pick up where you left off — nine questions, one decision-ready brief.
      {:else if progress.answered >= progress.total}
        You’ve covered every question. Continue to generate your research brief, or discard if you no longer need it.
      {:else}
        You’ve shaped {progress.answered} of {progress.total} questions. Continue and we’ll pick up
        {#if nextStep}
          at <strong>{nextStep.label}</strong>.
        {:else}
          where you left off.
        {/if}
      {/if}
    </p>
  </header>

  <div class="paused-body">
    <div class="progress-panel">
      <div class="progress-meta">
        <span class="progress-label">Consultation progress</span>
        <span class="progress-count" aria-live="polite">
          {progress.answered}<span class="progress-of">/{progress.total}</span>
        </span>
      </div>
      <div
        class="progress-track"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={progress.total}
        aria-valuenow={progress.answered}
        aria-label="Questions answered"
      >
        <div class="progress-fill" style="width: {progress.percent}%"></div>
      </div>
      <ol class="step-rail" aria-label="Question steps">
        {#each progress.steps as step, i (step.id)}
          {@const done = isStepAnswered(step, answers)}
          {@const current = !done && (i === 0 || isStepAnswered(progress.steps[i - 1], answers))}
          <li
            class="step-dot"
            class:done
            class:current
            title="{step.number} {step.label}"
          >
            <span class="sr-only">
              {step.number} {step.label}: {done ? 'answered' : current ? 'next' : 'upcoming'}
            </span>
          </li>
        {/each}
      </ol>
    </div>

    {#if decisionQuote}
      <blockquote class="decision-quote">
        <span class="quote-label">The decision</span>
        <p class="quote-text">“{decisionQuote}”</p>
      </blockquote>
    {/if}

    {#if previewSteps.length > 0}
      <ul class="answer-preview">
        {#each previewSteps as step, i (step.id)}
          <li class="preview-row" style="--delay: {0.08 + i * 0.06}s">
            <span class="preview-num">{step.number}</span>
            <div class="preview-copy">
              <span class="preview-label">{step.label}</span>
              <span class="preview-value">{stepPreview(step, answers)}</span>
            </div>
            <i class="check circle icon preview-check" aria-hidden="true"></i>
          </li>
        {/each}
        {#if progress.answered > previewSteps.length}
          <li class="preview-more">
            +{progress.answered - previewSteps.length} more answered
          </li>
        {/if}
      </ul>
    {/if}
  </div>

  <div class="paused-actions">
    <button
      type="button"
      class="btn-continue"
      disabled={isGenerating}
      aria-disabled={isGenerating}
      title={isGenerating ? 'Wait until generation finishes' : undefined}
      on:click={() => dispatch('continue')}
    >
      <i class="play icon" aria-hidden="true"></i>
      Continue consultation
    </button>
    <button type="button" class="btn-discard" on:click={() => dispatch('discard')}>
      <i class="trash icon" aria-hidden="true"></i>
      Discard
    </button>
  </div>
</section>

<style>
  .paused {
    position: relative;
    margin-top: var(--space-5);
    padding: var(--space-6);
    border-radius: var(--radius-card);
    background: var(--white);
    border: 1px solid var(--border-color);
    box-shadow: var(--shadow-card);
    overflow: hidden;
    animation: paused-in 0.45s ease-out both;
  }

  .paused::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: var(--accent-orange);
  }

  .paused-glow {
    position: absolute;
    top: -40%;
    right: -10%;
    width: min(420px, 55%);
    height: 70%;
    background: radial-gradient(
      ellipse at center,
      var(--accent-orange-soft) 0%,
      transparent 70%
    );
    pointer-events: none;
    opacity: 0.85;
  }

  .paused-header {
    position: relative;
    max-width: 52ch;
    margin-bottom: var(--space-6);
  }

  .paused-kicker {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    margin-bottom: var(--space-3);
  }

  .agent-chip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: var(--radius-input);
    background: var(--accent-orange-soft);
    color: var(--accent-orange-ink);
  }

  .agent-chip :global(i.icon) {
    margin: 0;
    font-size: 1rem;
  }

  .paused-title {
    margin: 0 0 var(--space-2) 0;
    font-size: var(--text-2xl);
    font-weight: 600;
    color: var(--grey);
    letter-spacing: -0.02em;
    line-height: 1.2;
    text-wrap: balance;
  }

  .paused-lede {
    margin: 0;
    font-size: var(--text-base);
    color: var(--grey-secondary);
    line-height: 1.6;
  }

  .paused-lede :global(strong) {
    color: var(--grey);
    font-weight: 500;
  }

  .paused-body {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
    margin-bottom: var(--space-6);
  }

  .progress-panel {
    padding: var(--space-4);
    border-radius: var(--radius-input);
    background: var(--greyish);
    border: 1px solid var(--border-color);
  }

  .progress-meta {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--space-3);
    margin-bottom: var(--space-3);
  }

  .progress-label {
    font-size: var(--text-sm);
    font-weight: 500;
    color: var(--grey);
  }

  .progress-count {
    font-size: var(--text-xl);
    font-weight: 600;
    color: var(--accent-orange-ink);
    font-variant-numeric: tabular-nums;
  }

  .progress-of {
    font-size: var(--text-sm);
    font-weight: 500;
    color: var(--grey-secondary);
  }

  .progress-track {
    height: 6px;
    border-radius: var(--radius-pill);
    background: var(--white);
    border: 1px solid var(--border-color);
    overflow: hidden;
    margin-bottom: var(--space-3);
  }

  .progress-fill {
    height: 100%;
    border-radius: inherit;
    background: var(--accent-orange);
    transition: width 0.4s ease-out;
  }

  .step-rail {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    gap: 0.35rem;
    flex-wrap: wrap;
  }

  .step-dot {
    width: 0.65rem;
    height: 0.65rem;
    border-radius: 50%;
    background: var(--white);
    border: 1.5px solid var(--border-color);
    transition: background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
  }

  .step-dot.done {
    background: var(--accent-orange);
    border-color: var(--accent-orange);
  }

  .step-dot.current {
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
    transform: scale(1.15);
  }

  .decision-quote {
    margin: 0;
    padding: var(--space-4) var(--space-5);
    border-left: 3px solid var(--accent-orange);
    background: var(--accent-orange-soft);
    border-radius: 0 var(--radius-input) var(--radius-input) 0;
    animation: paused-in 0.5s ease-out 0.08s both;
  }

  .quote-label {
    display: block;
    font-size: var(--text-xs);
    font-weight: 500;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--accent-orange-ink);
    margin-bottom: var(--space-2);
  }

  .quote-text {
    margin: 0;
    font-size: var(--text-lg);
    font-weight: 500;
    color: var(--grey);
    line-height: 1.45;
    text-wrap: pretty;
  }

  .answer-preview {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .preview-row {
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: var(--space-3);
    align-items: start;
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius-input);
    border: 1px solid var(--border-color);
    background: var(--white);
    animation: paused-in 0.45s ease-out var(--delay, 0.1s) both;
  }

  .preview-num {
    font-size: var(--text-xs);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: var(--accent-orange-ink);
    background: var(--accent-orange-soft);
    padding: 0.2rem 0.45rem;
    border-radius: var(--radius-pill);
    line-height: 1.2;
  }

  .preview-copy {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    min-width: 0;
  }

  .preview-label {
    font-size: var(--text-xs);
    font-weight: 500;
    color: var(--grey-secondary);
  }

  .preview-value {
    font-size: var(--text-sm);
    color: var(--grey);
    line-height: 1.45;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .preview-check {
    color: var(--accent-orange);
    margin: 0 !important;
    opacity: 0.85;
  }

  .preview-more {
    font-size: var(--text-sm);
    color: var(--grey-secondary);
    padding-left: var(--space-2);
  }

  .paused-actions {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-3);
    align-items: center;
  }

  .btn-continue,
  .btn-discard {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    border: 0;
    cursor: pointer;
    border-radius: var(--radius-cta);
    padding: var(--space-3) var(--space-5);
    font-size: var(--text-sm);
    font-weight: 500;
    font-family: inherit;
    transition: background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease,
      border-color 0.2s ease, opacity 0.2s ease;
  }

  .btn-continue {
    background: var(--primary);
    color: var(--white);
    box-shadow: var(--shadow-cta);
  }

  .btn-continue:hover:not(:disabled) {
    background: var(--darkprimary);
  }

  .btn-continue:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }

  .btn-continue:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }

  .btn-discard {
    background: transparent;
    color: var(--status-danger-ink);
    border: 1px solid var(--border-color);
  }

  .btn-discard:hover {
    background: var(--status-danger-soft);
    border-color: var(--status-danger);
  }

  .btn-discard:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  @keyframes paused-in {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    .paused {
      padding: var(--space-5) var(--space-4);
    }

    .paused-actions {
      flex-direction: column;
      align-items: stretch;
    }

    .btn-continue,
    .btn-discard {
      justify-content: center;
    }
  }
</style>
